import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNotifications } from "@/context/NotificationsContext";
import { useBetSlip } from "@/context/BetSlipContext";
import { enablePush, currentPushState } from "@/lib/push";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import ChatContextPicker from "@/components/ChatContextPicker";
import ChatContextCard from "@/components/ChatContextCard";
import {
  MessagesSquare, Users, User, Send, Paperclip, Sparkles, ArrowLeft, Plus, X, Bell, ListChecks, Check,
} from "lucide-react";

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const diff = (now - d) / 1000;
  if (diff < 60) return "now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return d.toLocaleDateString();
}
function clock(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

// ---- new direct chat dialog ----
function NewChatDialog({ open, onClose, onStart }) {
  const [mates, setMates] = useState([]);
  useEffect(() => {
    if (open) api.get("/chat/mates").then((r) => setMates(r.data)).catch(() => setMates([]));
  }, [open]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4" data-testid="new-chat-dialog" onClick={onClose}>
      <div className="glass w-full sm:max-w-sm rounded-t-3xl sm:rounded-3xl p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <span className="font-display font-extrabold">Message a mate</span>
          <Button variant="ghost" size="icon" className="rounded-full" onClick={onClose} data-testid="new-chat-close"><X className="h-4 w-4" /></Button>
        </div>
        {mates.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">
            You can message mates from your groups. Join or create a group first.
          </p>
        ) : (
          <div className="space-y-1 max-h-72 overflow-y-auto">
            {mates.map((m) => (
              <button key={m.id} data-testid={`mate-${m.id}`} onClick={() => onStart(m)}
                className="w-full flex items-center gap-3 p-2.5 rounded-2xl hover:bg-secondary transition-colors text-left">
                <div className="h-9 w-9 rounded-full bg-primary/15 flex items-center justify-center shrink-0">
                  <User className="h-4 w-4 text-primary" />
                </div>
                <span className="font-medium text-sm">{m.name}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ---- poll card (interactive) ----
function PollCard({ msg, onVote }) {
  const ctx = msg.context;
  const state = ctx.state || { tally: {}, total: 0, my_vote: null };
  const total = state.total || 0;
  return (
    <div className="rounded-xl bg-background/70 border border-border/50 p-3 mb-1.5 w-full" data-testid="poll-card">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-muted-foreground mb-2">
        <ListChecks className="h-3 w-3" /> Poll
      </div>
      <div className="font-display font-bold text-sm mb-2 text-foreground">{ctx.question}</div>
      <div className="space-y-1.5">
        {ctx.options.map((o) => {
          const votes = state.tally?.[o.key] || 0;
          const pct = total ? Math.round((votes / total) * 100) : 0;
          const mine = state.my_vote === o.key;
          return (
            <button key={o.key} data-testid={`poll-option-${o.key}`} onClick={() => onVote(msg.id, o.key)}
              className={`relative w-full text-left rounded-lg overflow-hidden border transition-colors ${mine ? "border-primary" : "border-border/60 hover:border-primary/50"}`}>
              <div className="absolute inset-y-0 left-0 bg-primary/15" style={{ width: `${pct}%` }} />
              <div className="relative flex items-center justify-between px-2.5 py-1.5 text-sm text-foreground">
                <span className="flex items-center gap-1.5 font-medium">
                  {mine && <Check className="h-3.5 w-3.5 text-primary" />}{o.label}
                </span>
                <span className="text-xs text-muted-foreground">{votes} · {pct}%</span>
              </div>
            </button>
          );
        })}
      </div>
      <div className="text-[10px] text-muted-foreground mt-2">{total} vote{total === 1 ? "" : "s"} · tap to change</div>
    </div>
  );
}

// ---- slip card (from AI Mate) ----
function SlipCard({ ctx }) {
  const { toggleLeg, has } = useBetSlip();
  const legs = ctx.legs || [];
  const addAll = () => {
    legs.forEach((l, i) => {
      const leg = {
        id: `aislip|${l.player || l.selection}|${i}|${l.selection}`,
        player: l.player || l.selection,
        prop: l.market || l.selection,
        odd: l.odds && Number(l.odds) > 1 ? Number(l.odds) : 2.0,
        prob: null,
        tournament: l.tournament || "",
      };
      if (!has(leg.id)) toggleLeg(leg);
    });
    toast.success("Added to your bet slip");
  };
  return (
    <div className="rounded-xl bg-primary/10 border border-primary/20 p-3 mb-1.5 w-full" data-testid="slip-card">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-primary mb-1.5">
        <Sparkles className="h-3 w-3" /> Bet slip from your chat
      </div>
      {ctx.summary && <p className="text-sm text-foreground mb-2">{ctx.summary}</p>}
      <div className="space-y-1 mb-2.5">
        {legs.map((l, i) => (
          <div key={i} className="flex items-center justify-between gap-2 text-sm">
            <span className="text-foreground truncate">{l.selection}</span>
            {l.odds && Number(l.odds) > 1 && <span className="text-primary font-semibold shrink-0">@{Number(l.odds).toFixed(2)}</span>}
          </div>
        ))}
      </div>
      <Button size="sm" className="w-full rounded-full gap-1.5" onClick={addAll} data-testid="add-slip-to-betslip">
        <Plus className="h-4 w-4" /> Add {legs.length} legs to bet slip
      </Button>
    </div>
  );
}

// ---- one message bubble ----
function Bubble({ msg, mine, showName, navigate, onVote }) {
  const ai = msg.is_ai;
  const type = msg.context?.type;
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid="chat-message">
      <div className={`max-w-[80%] space-y-1`}>
        {showName && !mine && (
          <div className={`text-[11px] font-semibold px-1 ${ai ? "text-primary" : "text-muted-foreground"}`}>
            {ai && <Sparkles className="h-3 w-3 inline mr-1 -mt-0.5" />}{msg.sender_name}
          </div>
        )}
        <div className={`rounded-2xl px-3.5 py-2 text-sm leading-snug break-words ${
          mine ? "bg-primary text-primary-foreground rounded-br-md"
          : ai ? "bg-primary/10 text-foreground rounded-bl-md border border-primary/20"
          : "bg-secondary text-foreground rounded-bl-md"}`}>
          {type === "poll" ? <PollCard msg={msg} onVote={onVote} />
            : type === "slip" ? <SlipCard ctx={msg.context} />
            : msg.context ? <ChatContextCard context={msg.context} mine={mine} navigate={navigate} /> : null}
          {msg.text && <p className="whitespace-pre-wrap">{msg.text}</p>}
        </div>
        <div className={`text-[10px] text-muted-foreground px-1 ${mine ? "text-right" : ""}`}>{clock(msg.created_at)}</div>
      </div>
    </div>
  );
}

export default function Mates() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { addHandler, connected, refreshUnread } = useNotifications();
  const [convs, setConvs] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reads, setReads] = useState({});
  const [typing, setTyping] = useState(null); // {name}
  const [text, setText] = useState("");
  const [pending, setPending] = useState(null); // pending context attachment
  const [pickerOpen, setPickerOpen] = useState(false);
  const [newChatOpen, setNewChatOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [pushState, setPushState] = useState("off");

  const activeRef = useRef(null);
  const scrollRef = useRef(null);
  const typingSentRef = useRef(0);
  const typingTimerRef = useRef(null);
  activeRef.current = activeId;

  const active = convs.find((c) => c.id === activeId);

  const loadConvs = useCallback(async () => {
    try {
      const r = await api.get("/chat/conversations");
      setConvs(r.data);
      return r.data;
    } catch { return []; }
  }, []);

  const openConv = useCallback(async (id) => {
    setActiveId(id);
    setMessages([]);
    setTyping(null);
    try {
      const [m, rd] = await Promise.all([
        api.get(`/chat/conversations/${id}/messages`),
        api.get(`/chat/conversations/${id}/reads`),
      ]);
      setMessages(m.data);
      setReads(rd.data);
      await api.post(`/chat/conversations/${id}/read`);
      setConvs((prev) => prev.map((c) => (c.id === id ? { ...c, unread: 0 } : c)));
      refreshUnread();
    } catch (e) { /* noop */ }
  }, [refreshUnread]);

  // initial load
  useEffect(() => { loadConvs(); }, [loadConvs]);

  // scroll to bottom on new messages
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, typing]);

  // polling fallback: if SSE is buffered/dropped (e.g. behind a CDN/proxy in prod),
  // this guarantees the open thread + list still update without a manual refresh.
  useEffect(() => {
    const listPoll = setInterval(() => { loadConvs(); }, 8000);
    return () => clearInterval(listPoll);
  }, [loadConvs]);

  useEffect(() => {
    if (!activeId) return;
    let cancelled = false;
    const poll = setInterval(async () => {
      try {
        const [m, rd] = await Promise.all([
          api.get(`/chat/conversations/${activeId}/messages`),
          api.get(`/chat/conversations/${activeId}/reads`),
        ]);
        if (cancelled) return;
        setReads(rd.data);
        setMessages((prev) => {
          const ids = new Set(prev.map((x) => x.id));
          const incoming = m.data.filter((x) => !ids.has(x.id));
          if (!incoming.length) {
            // no new messages: only update if a poll's vote tally actually changed
            const byId = Object.fromEntries(m.data.map((x) => [x.id, x]));
            let changed = false;
            const next = prev.map((p) => {
              if (p.context?.type === "poll" && byId[p.id]) {
                const a = JSON.stringify(p.context?.state);
                const b = JSON.stringify(byId[p.id].context?.state);
                if (a !== b) { changed = true; return byId[p.id]; }
              }
              return p;
            });
            return changed ? next : prev;
          }
          if (incoming.some((x) => x.sender_id !== user?.id)) {
            api.post(`/chat/conversations/${activeId}/read`).catch(() => {});
          }
          return [...prev, ...incoming];
        });
      } catch { /* noop */ }
    }, 4000);
    return () => { cancelled = true; clearInterval(poll); };
  }, [activeId, user?.id]);

  // subscribe to the app-wide chat stream (shared EventSource in NotificationsContext)
  useEffect(() => {
    const off = addHandler((ev) => {
      if (ev.type === "message") {
        const cid = ev.conversation_id;
        const msg = ev.message;
        if (cid === activeRef.current) {
          setMessages((prev) => (prev.some((m) => m.id === msg.id) ? prev : [...prev, msg]));
          if (msg.sender_id !== user?.id) api.post(`/chat/conversations/${cid}/read`).catch(() => {});
        }
        setConvs((prev) => {
          const found = prev.find((c) => c.id === cid);
          const preview = { text: msg.text || "shared something", sender_name: msg.sender_name, at: msg.created_at };
          if (!found) { loadConvs(); return prev; }
          return prev.map((c) => c.id === cid ? {
            ...c, last_message: preview, last_message_at: msg.created_at,
            unread: cid === activeRef.current ? 0 : (msg.sender_id === user?.id ? c.unread : (c.unread || 0) + 1),
          } : c);
        });
      } else if (ev.type === "poll_update") {
        if (ev.conversation_id === activeRef.current) {
          setMessages((prev) => prev.map((m) => m.id === ev.message_id
            ? { ...m, context: { ...m.context, state: ev.state } } : m));
        }
      } else if (ev.type === "typing") {
        if (ev.conversation_id === activeRef.current && ev.user_id !== user?.id) {
          setTyping({ name: ev.name });
          clearTimeout(typingTimerRef.current);
          typingTimerRef.current = setTimeout(() => setTyping(null), 3500);
        }
      } else if (ev.type === "read") {
        if (ev.conversation_id === activeRef.current)
          setReads((prev) => ({ ...prev, [ev.user_id]: ev.at }));
      }
    });
    return off;
  }, [addHandler, user?.id, loadConvs]);

  // push state
  useEffect(() => { currentPushState().then(setPushState); }, []);
  const turnOnPush = async () => {
    try {
      await enablePush();
      setPushState("on");
      await api.post("/push/test").catch(() => {});
      toast.success("Alerts on. We'll ping you about messages and kickoffs.");
    } catch (e) {
      toast.error(e.message || "Could not turn on alerts.");
    }
  };

  const onInput = (v) => {
    setText(v);
    const now = Date.now();
    if (activeId && now - typingSentRef.current > 2500) {
      typingSentRef.current = now;
      api.post(`/chat/conversations/${activeId}/typing`).catch(() => {});
    }
  };

  const send = async (askAi = false) => {
    const t = text.trim();
    if ((!t && !pending) || !activeId || sending) return;
    setSending(true);
    try {
      if (pending && pending.type === "poll") {
        await api.post(`/chat/conversations/${activeId}/polls`, {
          question: pending.question, options: pending.options, fixture: pending.fixture,
        });
      } else {
        const res = await api.post(`/chat/conversations/${activeId}/messages`, {
          text: t, context: pending, ask_ai: askAi,
        });
        // optimistic append so the sender sees it instantly (SSE echo will dedupe by id)
        if (res?.data?.id) {
          const sent = res.data;
          setMessages((prev) => (prev.some((m) => m.id === sent.id) ? prev : [...prev, sent]));
        }
      }
      setText("");
      setPending(null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setSending(false); }
  };

  const vote = async (messageId, optionKey) => {
    try {
      const r = await api.post(`/chat/polls/${messageId}/vote`, { option_key: optionKey });
      setMessages((prev) => prev.map((m) => m.id === messageId
        ? { ...m, context: { ...m.context, state: r.data } } : m));
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const buildSlip = async () => {
    if (!activeId) return;
    const tid = toast.loading("AI Mate is building a slip from your chat...");
    try {
      await api.post(`/chat/conversations/${activeId}/build-slip`);
      toast.success("AI Mate posted a slip. Tap 'Add to bet slip' on it.", { id: tid });
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail), { id: tid });
    }
  };

  const startDirect = async (mate) => {
    try {
      const r = await api.post("/chat/conversations/direct", { user_id: mate.id });
      setNewChatOpen(false);
      await loadConvs();
      openConv(r.data.id);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  // read-receipt for my last message
  const myLast = [...messages].reverse().find((m) => m.sender_id === user?.id);
  const seenByOthers = myLast && active
    ? active.members.filter((m) => m.id !== user?.id && reads[m.id] && reads[m.id] >= myLast.created_at)
    : [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl flex items-center gap-2">
            <MessagesSquare className="h-8 w-8 text-primary" /> Mates
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Chat with your groups, share picks and ask the AI Mate. {connected
              ? <span className="text-[#34C759] font-medium">Live</span>
              : <span className="text-muted-foreground">connecting...</span>}
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-[300px_1fr] gap-4 h-[calc(100vh-230px)] min-h-[420px]">
        {/* conversation list */}
        <div className={`glass rounded-3xl p-3 overflow-y-auto ${activeId ? "hidden md:block" : "block"}`} data-testid="conversation-list">
          <Button onClick={() => setNewChatOpen(true)} data-testid="new-chat-btn"
            className="w-full rounded-full mb-3 gap-2" size="sm">
            <Plus className="h-4 w-4" /> New message
          </Button>
          {convs.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-8 px-3">
              No chats yet. Your groups appear here automatically, or start a direct message with a mate.
            </p>
          )}
          <div className="space-y-1">
            {convs.map((c) => (
              <button key={c.id} data-testid={`conversation-${c.id}`} onClick={() => openConv(c.id)}
                className={`w-full flex items-center gap-3 p-2.5 rounded-2xl text-left transition-colors ${
                  c.id === activeId ? "bg-secondary" : "hover:bg-secondary/60"}`}>
                <div className={`h-10 w-10 rounded-full flex items-center justify-center shrink-0 ${
                  c.type === "group" ? "bg-primary/15" : "bg-[#0A84FF]/15"}`}>
                  {c.type === "group" ? <Users className="h-5 w-5 text-primary" /> : <User className="h-5 w-5 text-[#0A84FF]" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold text-sm truncate">{c.title}</span>
                    <span className="text-[10px] text-muted-foreground shrink-0">{timeAgo(c.last_message_at)}</span>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs text-muted-foreground truncate">
                      {c.last_message ? `${c.last_message.sender_name}: ${c.last_message.text}` : "No messages yet"}
                    </span>
                    {c.unread > 0 && (
                      <Badge className="rounded-full h-5 min-w-5 px-1.5 text-[10px] bg-primary text-primary-foreground border-0" data-testid={`unread-${c.id}`}>
                        {c.unread}
                      </Badge>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* thread */}
        <div className={`glass rounded-3xl flex flex-col overflow-hidden ${activeId ? "flex" : "hidden md:flex"}`} data-testid="chat-thread">
          {!active ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center p-8 text-muted-foreground">
              <MessagesSquare className="h-12 w-12 mb-3 opacity-40" />
              <p className="font-medium">Pick a chat to start talking</p>
              <p className="text-sm">Share match predictions, picks, bets and get the AI Mate's take.</p>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 p-4 border-b border-border">
                <Button variant="ghost" size="icon" className="rounded-full md:hidden" onClick={() => setActiveId(null)} data-testid="thread-back">
                  <ArrowLeft className="h-4 w-4" />
                </Button>
                <div className={`h-9 w-9 rounded-full flex items-center justify-center ${active.type === "group" ? "bg-primary/15" : "bg-[#0A84FF]/15"}`}>
                  {active.type === "group" ? <Users className="h-4 w-4 text-primary" /> : <User className="h-4 w-4 text-[#0A84FF]" />}
                </div>
                <div className="min-w-0">
                  <div className="font-display font-bold truncate" data-testid="thread-title">{active.title}</div>
                  <div className="text-[11px] text-muted-foreground truncate">
                    {active.type === "group" ? `${active.members.length} mates + AI Mate` : "Direct message"}
                  </div>
                </div>
              </div>

              <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="messages-scroll">
                {messages.map((m) => (
                  <Bubble key={m.id} msg={m} mine={m.sender_id === user?.id}
                    showName={active.type === "group"} navigate={navigate} onVote={vote} />
                ))}
                {myLast && seenByOthers.length > 0 && (
                  <div className="text-[10px] text-muted-foreground text-right pr-1" data-testid="read-receipt">
                    {active.type === "group" ? `Seen by ${seenByOthers.length}` : "Seen"}
                  </div>
                )}
                {typing && (
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground pl-1" data-testid="typing-indicator">
                    <span className="font-medium">{typing.name}</span> is typing
                    <span className="flex gap-0.5">
                      <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground animate-bounce" style={{ animationDelay: "0ms" }} />
                      <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground animate-bounce" style={{ animationDelay: "150ms" }} />
                      <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground animate-bounce" style={{ animationDelay: "300ms" }} />
                    </span>
                  </div>
                )}
              </div>

              {/* composer */}
              <div className="border-t border-border p-3 space-y-2">
                {pushState === "off" && (
                  <button onClick={turnOnPush} data-testid="enable-push-btn"
                    className="w-full flex items-center justify-center gap-2 text-xs font-medium text-primary bg-primary/10 rounded-full py-2 hover:bg-primary/15 transition-colors">
                    <Bell className="h-3.5 w-3.5" /> Turn on alerts for messages and kickoffs
                  </button>
                )}
                {pending && (
                  <div className="flex items-center gap-2 bg-secondary/60 rounded-2xl p-2" data-testid="pending-context">
                    <div className="flex-1 min-w-0">
                      {pending.type === "poll" ? (
                        <div className="flex items-center gap-2 text-sm px-1">
                          <ListChecks className="h-4 w-4 text-primary shrink-0" />
                          <span className="truncate">Poll: {pending.question || (pending.fixture ? `${pending.fixture.home} vs ${pending.fixture.away}` : "custom")}</span>
                        </div>
                      ) : (
                        <ChatContextCard context={pending} mine={false} navigate={navigate} compact />
                      )}
                    </div>
                    <Button variant="ghost" size="icon" className="rounded-full h-7 w-7 shrink-0" onClick={() => setPending(null)} data-testid="pending-context-remove">
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="icon" className="rounded-full shrink-0" onClick={() => setPickerOpen(true)} data-testid="attach-btn" title="Attach a match, pick, bet, competition or poll">
                    <Paperclip className="h-5 w-5" />
                  </Button>
                  <Button variant="ghost" size="icon" className="rounded-full shrink-0 hidden sm:flex" onClick={buildSlip} data-testid="build-slip-btn" title="Let AI Mate build a bet slip from this chat">
                    <ListChecks className="h-5 w-5" />
                  </Button>
                  <Input value={text} onChange={(e) => onInput(e.target.value)} data-testid="message-input"
                    onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(false); } }}
                    placeholder="Message your mates... (type @ai to ask the AI Mate)" className="rounded-full" />
                  <Button variant="ghost" size="icon" className="rounded-full shrink-0 text-primary" onClick={() => send(true)} data-testid="ask-ai-btn" title="Ask the AI Mate">
                    <Sparkles className="h-5 w-5" />
                  </Button>
                  <Button size="icon" className="rounded-full shrink-0" onClick={() => send(false)} disabled={sending || (!text.trim() && !pending)} data-testid="send-btn">
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <ChatContextPicker open={pickerOpen} onClose={() => setPickerOpen(false)} onPick={(ctx) => { setPending(ctx); setPickerOpen(false); }} />
      <NewChatDialog open={newChatOpen} onClose={() => setNewChatOpen(false)} onStart={startDirect} />
    </div>
  );
}
